/**
 * Employee self-service API client. Wraps the /v1/me/* endpoints with
 * typed responses pulled from @kora/types. Mirrors the style of
 * apps/web/src/api/payrollRuns.ts (apiRequest for JSON, apiDownload
 * for binary downloads).
 */
import type {
  ChangePasswordInput,
  MePayslipsListResponse,
  MeProfileResponse,
  MyDocumentsListResponse,
  RevokeAllSessionsResponse,
  SessionListResponse,
  UpdateMyProfileInput,
} from '@kora/types';
import { apiDownload, apiDownloadBlob, apiRequest } from './client.js';

/**
 * GET /v1/me/profile — current employee's user + employee record.
 * 403 if the JWT belongs to a non-employee role; 403 if the
 * employee user has no employeeId link (provisioning is broken).
 */
export function getMeProfile(): Promise<MeProfileResponse> {
  return apiRequest<MeProfileResponse>('/v1/me/profile');
}

/**
 * GET /v1/me/payslips — all of the current employee's payslips
 * from runs in {approved, paid, voided}. Newest first.
 */
export function listMyPayslips(): Promise<MePayslipsListResponse> {
  return apiRequest<MePayslipsListResponse>('/v1/me/payslips');
}

/**
 * PATCH /v1/me/profile — narrow-scope self-edit for contact details
 * + next-of-kin. Server is the source of truth on what's editable
 * (UpdateMyProfileInput is .strict() in @kora/types); a 400 here
 * means the caller smuggled a disallowed field.
 *
 * Empty body `{}` is a server-side no-op (no audit write); UI code
 * should still avoid sending it.
 */
export function updateMyProfile(
  body: UpdateMyProfileInput,
): Promise<MeProfileResponse> {
  return apiRequest<MeProfileResponse>('/v1/me/profile', {
    method: 'PATCH',
    body,
  });
}

/**
 * POST /v1/me/password/change — change the current user's password.
 * 204 on success (no body). Errors arrive as ApiError with a body
 * shape of `{ error: 'invalid_credentials' | 'weak_password' |
 * 'same_password', reason?, message? }` — callers map these to
 * inline field errors. Available to any authenticated role.
 */
export function changeMyPassword(body: ChangePasswordInput): Promise<void> {
  return apiRequest<void>('/v1/me/password/change', {
    method: 'POST',
    body,
  });
}

/**
 * GET /v1/me/sessions — list the caller's active refresh tokens for
 * the /security/sessions page.
 */
export function listMySessions(): Promise<SessionListResponse> {
  return apiRequest<SessionListResponse>('/v1/me/sessions');
}

/**
 * DELETE /v1/me/sessions/:id — revoke a single non-current session.
 * Returns void on 204. ApiError 400 `cannot_revoke_current_session`
 * if the target is the current session (UI disables the button there).
 */
export function revokeMySession(id: string): Promise<void> {
  return apiRequest<void>(`/v1/me/sessions/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * DELETE /v1/me/sessions — revoke every other active session for the
 * current user (the current session is preserved when the refresh
 * cookie is present). Returns `{ revokedCount }`.
 */
export function revokeAllOtherSessions(): Promise<RevokeAllSessionsResponse> {
  return apiRequest<RevokeAllSessionsResponse>('/v1/me/sessions', {
    method: 'DELETE',
  });
}

/**
 * GET /v1/me/documents — list documents HR has uploaded for the
 * caller. Read-only surface; uploads + deletes stay on the admin
 * side.
 */
export function listMyDocuments(): Promise<MyDocumentsListResponse> {
  return apiRequest<MyDocumentsListResponse>('/v1/me/documents');
}

/**
 * GET /v1/me/documents/:id/download — fetch the bytes for one of
 * the caller's own documents and return them as a Blob plus the
 * original filename (parsed from content-disposition).
 *
 * The route component is responsible for triggering the actual
 * browser download (createObjectURL + click + revoke). Returning
 * the Blob from the API client (rather than triggering download
 * inside it) gives the route the loading-state hook it needs and
 * keeps the side effect — opening a save dialog — out of the
 * data layer.
 *
 * This is the polish the admin documents download (see
 * buildDocumentDownloadUrl in api/documents.ts) should eventually
 * migrate to: the admin uses `<a href>` which works only because
 * the refresh cookie is sent with browser-initiated navigation —
 * a hidden coupling that would break under pure-JWT auth. Don't
 * change the admin side here; out of scope.
 */
export function downloadMyDocument(
  id: string,
): Promise<{ blob: Blob; filename: string }> {
  return apiDownloadBlob(
    `/v1/me/documents/${encodeURIComponent(id)}/download`,
  );
}

/**
 * GET /v1/me/payslips/:id/pdf — download an authenticated PDF of
 * the named payslip. Server enforces ownership; a 404 here means
 * either the payslip doesn't exist OR doesn't belong to the
 * current employee (no existence leak).
 */
export function downloadMyPayslipPdf(payslipId: string): Promise<void> {
  return apiDownload(
    `/v1/me/payslips/${encodeURIComponent(payslipId)}/pdf`,
    'payslip.pdf',
  );
}
